import Phaser from 'phaser';
import type { Hole } from './Playfield';
import { GRID_COLS, HOLE_RADIUS, CELL_H } from './Playfield';
import { palette } from '../design/palette';
import { LOGICAL_HEIGHT } from '../dimensions';
import { computeVisibleRowRange } from './holeWindow';

const TEX_KEY = 'hole-sprite';
const TEX_SIZE = 32;
const TEX_RIM_R = 16;
const TEX_FILL_R = 14;
const BUFFER_ROWS = 4;
const DISPLAY_DIAMETER = (HOLE_RADIUS + 1) * 2;  // 20 — preserves 1px rim from original
const POOL_SIZE = GRID_COLS * (Math.ceil(LOGICAL_HEIGHT / CELL_H) + 2 * BUFFER_ROWS + 4);

function ensureHoleTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(TEX_KEY)) return;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  g.fillStyle(palette.holeRim, 1);
  g.fillCircle(TEX_SIZE / 2, TEX_SIZE / 2, TEX_RIM_R);
  g.fillStyle(palette.holeFill, 1);
  g.fillCircle(TEX_SIZE / 2, TEX_SIZE / 2, TEX_FILL_R);
  g.generateTexture(TEX_KEY, TEX_SIZE, TEX_SIZE);
  g.destroy();
}

export class HoleSpriteRenderer {
  private pool: Phaser.GameObjects.Sprite[] = [];
  private container: Phaser.GameObjects.Container;
  private holesByRow: ReadonlyArray<ReadonlyArray<Hole>>;
  private clearCells: Set<string>;
  private lastStart = -1;
  private lastEnd = -1;
  private onUpdate: () => void;

  constructor(
    private scene: Phaser.Scene,
    holes: ReadonlyArray<Hole>,
    clearCells: Set<string>,
    depth: number,
  ) {
    ensureHoleTexture(scene);

    this.clearCells = clearCells;

    const grouped: Hole[][] = [];
    for (const h of holes) {
      (grouped[h.row] ??= []).push(h);
    }
    this.holesByRow = grouped;

    this.container = scene.add.container(0, 0).setDepth(depth);

    for (let i = 0; i < POOL_SIZE; i++) {
      const s = scene.add.sprite(0, 0, TEX_KEY)
        .setDisplaySize(DISPLAY_DIAMETER, DISPLAY_DIAMETER)
        .setVisible(false);
      this.pool.push(s);
      this.container.add(s);
    }

    this.refresh();
    this.onUpdate = (): void => this.refresh();
    scene.events.on('update', this.onUpdate);
  }

  private refresh(): void {
    const { startRow, endRow } = computeVisibleRowRange(
      this.scene.cameras.main.scrollY,
      LOGICAL_HEIGHT,
      BUFFER_ROWS,
    );
    if (startRow === this.lastStart && endRow === this.lastEnd) return;
    this.lastStart = startRow;
    this.lastEnd = endRow;

    let poolIdx = 0;
    outer: for (let row = startRow; row <= endRow; row++) {
      const rowHoles = this.holesByRow[row];
      if (!rowHoles) continue;
      for (const h of rowHoles) {
        if (this.clearCells.has(`${h.col},${h.row}`)) continue;
        if (poolIdx >= this.pool.length) break outer;  // pool exhausted — still run cleanup below
        const s = this.pool[poolIdx++];
        s.setPosition(h.x, h.y).setVisible(true);
      }
    }
    for (let i = poolIdx; i < this.pool.length; i++) {
      this.pool[i].setVisible(false);
    }
  }

  /** Re-window after the level's clear-cell set changed without rebuilding the pool. */
  public setClearCells(clearCells: Set<string>): void {
    this.clearCells = clearCells;
    this.lastStart = -1;  // force re-assign
    this.refresh();
  }

  /** Returns the total pool capacity for debug inspection. */
  public debugPoolSize(): number {
    return this.pool.length;
  }

  public destroy(): void {
    this.scene.events.off('update', this.onUpdate);
    this.container.destroy();
    this.pool.length = 0;
  }
}
